import type { RefObject } from 'react'
import { useEffect } from 'react'

import { getNextIndex } from '~/components/player/next-index'
import type { AudioChannel, PlayerState } from '~/components/player/types'
import { updateMediaSession } from '~/components/player/utils'
import { streamUrl } from '~/lib/cover-art'
import { isIOS } from '~/lib/platform'
import { replayGainToLinear } from '~/lib/replay-gain'
import { safePlay } from '~/lib/safe-play'

interface UseCrossfadeOptions {
  channelARef: RefObject<AudioChannel | null>
  channelBRef: RefObject<AudioChannel | null>
  activeChannelRef: RefObject<'A' | 'B'>
  crossfadeActiveRef: RefObject<boolean>
  crossfadeTimerRef: RefObject<ReturnType<typeof setTimeout> | null>
  stateRef: RefObject<PlayerState>
  currentTrackIdRef: RefObject<number | null>
  scrobbledRef: RefObject<boolean>
  ensureAudioContext: () => AudioContext | null
  setState: (updater: PlayerState | ((prev: PlayerState) => PlayerState)) => void
}

export function useCrossfade({
  channelARef,
  channelBRef,
  activeChannelRef,
  crossfadeActiveRef,
  crossfadeTimerRef,
  stateRef,
  currentTrackIdRef,
  scrobbledRef,
  ensureAudioContext,
  setState,
}: UseCrossfadeOptions): void {
  // Attaches one timeupdate listener per channel for the life of the player. All
  // player state is read through `stateRef` inside the handler so the listener
  // sees live values; depending on that state instead would detach and reattach
  // the listeners on every tick of playback.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    const chA = channelARef.current
    const chB = channelBRef.current
    if (!chA || !chB) return

    // iOS plays the bare element (no AudioContext), so crossfade — which needs
    // Web Audio gain ramps — is unavailable there. Tracks still advance gaplessly
    // via the onEnded channel swap in useAudioElement.
    const iosDevice = isIOS()

    const handleCrossfade = (e: Event) => {
      const s = stateRef.current
      if (iosDevice) return
      if (s.crossfadeDuration <= 0) return
      if (crossfadeActiveRef.current) return
      if (s.repeat === 'one') return

      // Only process timeupdate from the active channel
      const activeChannel = activeChannelRef.current === 'A' ? chA : chB
      if (e.target !== activeChannel.audio) return

      const inactiveChannel = activeChannelRef.current === 'A' ? chB : chA
      const audio = activeChannel.audio

      // Snap fade trigger to nearest bar boundary so the transition lands on a
      // musical phrase rather than an arbitrary point in time.
      const tempo = s.queue[s.currentIndex]?.tempo ?? null
      const barLen = tempo && tempo > 0 ? (4 * 60) / tempo : 0
      let threshold = s.crossfadeDuration
      if (barLen > 0 && barLen <= s.crossfadeDuration * 2) {
        const snapped = Math.round(s.crossfadeDuration / barLen) * barLen
        threshold = Math.max(barLen, snapped)
      }

      if (!audio.duration || audio.duration < threshold * 2) return

      const remaining = audio.duration - audio.currentTime
      if (remaining > threshold) return

      // Determine next track — pending-radio means no radio tracks buffered yet,
      // so skip crossfade and let onEnded handle advancement via pendingRadioAdvance.
      // Decision only: the actual transition here is a gain-ramped channel swap,
      // not a plain state update, so this path deliberately does not go through
      // advanceQueue. It bails on anything but a straight advance.
      const nextResult = getNextIndex(s)
      if (nextResult.type === 'stop' || nextResult.type === 'pending-radio') return
      const nextIndex = nextResult.index

      const nextTrack = s.queue[nextIndex]
      if (!nextTrack) return

      // Let silence padding handle interstitial transitions — crossfading into or
      // out of a DJ break would bleed music over the speech or eat the pre-gap.
      const currentTrack = s.queue[s.currentIndex]
      if (nextTrack.isInterstitial || currentTrack?.isInterstitial) return

      crossfadeActiveRef.current = true
      const ctx = ensureAudioContext()
      if (!ctx) {
        crossfadeActiveRef.current = false
        return
      }

      // Compute target gains
      const activeGain = activeChannel.gainNode
      const inactiveGain = inactiveChannel.gainNode
      const activeBaseGain = activeGain?.gain.value ?? 1.0
      const inactiveTargetGain = s.replayGainEnabled
        ? replayGainToLinear(
            nextTrack.replayGain?.trackGain,
            nextTrack.replayGain?.trackPeak,
            s.replayGainPreampDb
          )
        : 1.0
      const duration = s.crossfadeDuration

      // Load next track into inactive channel — reuse pre-loaded buffer if available
      const nextSrc = nextTrack.streamOverride ?? streamUrl(nextTrack.id)
      if (inactiveChannel.audio.getAttribute('data-preload') !== nextSrc) {
        inactiveChannel.audio.setAttribute('data-preload', nextSrc)
        inactiveChannel.audio.src = nextSrc
      }
      if (inactiveGain) {
        inactiveGain.gain.cancelScheduledValues(ctx.currentTime)
        inactiveGain.gain.setValueAtTime(0, ctx.currentTime)
      }
      safePlay(inactiveChannel.audio, 'crossfade')

      // Schedule gain ramps on the audio thread (sample-accurate, no main-thread jitter)
      const now = ctx.currentTime
      if (activeGain) {
        activeGain.gain.cancelScheduledValues(now)
        activeGain.gain.setValueAtTime(activeBaseGain, now)
        activeGain.gain.linearRampToValueAtTime(0, now + duration)
      }
      if (inactiveGain) {
        inactiveGain.gain.linearRampToValueAtTime(inactiveTargetGain, now + duration)
      }

      // Single timeout for channel swap after crossfade completes
      crossfadeTimerRef.current = setTimeout(() => {
        crossfadeTimerRef.current = null

        // Swap active reference
        activeChannelRef.current = activeChannelRef.current === 'A' ? 'B' : 'A'
        crossfadeActiveRef.current = false

        // Clean up old channel — pause immediately, defer resource release
        audio.pause()
        setTimeout(() => {
          audio.removeAttribute('src')
          audio.removeAttribute('data-preload')
          audio.load()
        }, 100)

        // Update state
        currentTrackIdRef.current = nextTrack.id
        scrobbledRef.current = false
        setState((prev) => ({
          ...prev,
          currentIndex: nextIndex,
          duration: inactiveChannel.audio.duration || 0,
        }))
        updateMediaSession(nextTrack)
      }, duration * 1000)
    }

    // Listen on BOTH audio elements so crossfade works regardless of which channel is active
    chA.audio.addEventListener('timeupdate', handleCrossfade)
    chB.audio.addEventListener('timeupdate', handleCrossfade)
    return () => {
      chA.audio.removeEventListener('timeupdate', handleCrossfade)
      chB.audio.removeEventListener('timeupdate', handleCrossfade)
      if (crossfadeTimerRef.current) {
        clearTimeout(crossfadeTimerRef.current)
        crossfadeTimerRef.current = null
      }
    }
  }, [ensureAudioContext])
}
